import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Settings, Building2, Plus, Pencil, Trash2, ShieldCheck, Bell, Clock, AlertTriangle,
  Plug, RotateCcw, Save, Sparkles
} from 'lucide-react';
import YapayZekaTab from './YapayZekaTab';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIRMA_TIPLERI = [
  { value: 'bakim_yuklenici', label: 'Bakım Yüklenici' },
  { value: 'a_tipi_muayene', label: 'A Tipi Muayene Kuruluşu' },
  { value: 'ekipman_muayene', label: 'Ekipman Muayene Kuruluşu' },
  { value: 'asansor_montaj', label: 'Asansör Montaj Firması' }
];

const EMPTY_FIRMA = { unvan: '', vergi_no: '', firma_tipi: 'bakim_yuklenici', turkak_akreditasyon_no: '', turkak_akreditasyon_bitis: '' };

const PERIYOT_LABELS = {
  asansor: 'Asansör', jenerator: 'Jeneratör', ups: 'UPS', trafo: 'Trafo',
  yangin_algilama: 'Yangın Algılama', sprinkler: 'Sprinkler', duman_tahliye: 'Duman Tahliye',
  hvac_klima: 'HVAC / Klima', buhar_kazani: 'Buhar Kazanı', lpg_tanki: 'LPG Tankı',
  forklift: 'Forklift', yapi_iskelesi: 'Yapı İskelesi', yuruyen_merdiven: 'Yürüyen Merdiven',
  topraklama: 'Topraklama', paratoner: 'Paratoner', katodik_koruma: 'Katodik Koruma',
  transpalet: 'Transpalet', erisim_platformu: 'Erişim Platformu',
  kompanzasyon: 'Kompanzasyon', boyler: 'Boyler', genlesme_tanki: 'Genleşme Tankı',
  kazan: 'Kazan', brulor: 'Brülör', kum_filtresi: 'Kum Filtresi', diger: 'Diğer'
};

const BILDIRIM_OLAYLARI = [
  { key: 'kirmizi_etiket', label: 'Kırmızı Etiket / Güvensiz Ekipman', icon: AlertTriangle, color: 'text-red-400' },
  { key: 'sla_ihlal', label: 'SLA İhlali', icon: Clock, color: 'text-orange-400' },
  { key: 'kontrol_yaklasan', label: 'Yaklaşan Periyodik Kontrol', icon: ShieldCheck, color: 'text-yellow-400' },
  { key: 'garanti_bitis', label: 'Garanti Bitiş Yaklaştı', icon: Bell, color: 'text-blue-400' }
];

const TABS = [
  { id: 'firmalar', label: 'Firma Yönetimi', icon: Building2 },
  { id: 'sla', label: 'SLA & Ceza', icon: Clock },
  { id: 'periyot', label: 'Periyot Varsayılanları', icon: ShieldCheck },
  { id: 'bildirim', label: 'Bildirim Tercihleri', icon: Bell },
  { id: 'entegrasyon', label: 'Entegrasyonlar', icon: Plug },
  { id: 'yapay_zeka', label: 'Yapay Zekâ', icon: Sparkles }
];

export default function PKAyarlar() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState('firmalar');

  // Firma state
  const [firmalar, setFirmalar] = useState([]);
  const [firmaLoading, setFirmaLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FIRMA });
  const [submitting, setSubmitting] = useState(false);

  // Ayarlar state
  const [ayarlar, setAyarlar] = useState(null);
  const [ayarlarLoading, setAyarlarLoading] = useState(true);
  const [ayarlarSaving, setAyarlarSaving] = useState(false);

  useEffect(() => {
    fetchFirmalar();
    fetchAyarlar();
  }, []);

  const fetchFirmalar = async () => {
    setFirmaLoading(true);
    try {
      const res = await axios.get(`${API}/pk/firmalar`, { withCredentials: true });
      setFirmalar(res.data);
    } catch (err) { console.error(err); }
    finally { setFirmaLoading(false); }
  };

  const fetchAyarlar = async () => {
    setAyarlarLoading(true);
    try {
      const res = await axios.get(`${API}/pk/ayarlar`, { withCredentials: true });
      setAyarlar(res.data);
    } catch (err) { console.error(err); }
    finally { setAyarlarLoading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.unvan) return;
    setSubmitting(true);
    try {
      if (editMode) {
        await axios.put(`${API}/pk/firmalar/${editId}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/pk/firmalar`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      setFormData({ ...EMPTY_FIRMA });
      fetchFirmalar();
    } catch (err) { toast.error('Hata: ' + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  const handleDeleteFirma = async (f) => {
    if (!window.confirm(`"${f.unvan}" firması silinecek. Geçmiş rapor ve kayıtlardaki referanslar korunur. Emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/pk/firmalar/${f.id}`, { withCredentials: true });
      toast.success('Firma silindi');
      fetchFirmalar();
    } catch (err) { toast.error('Silinemedi: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleEdit = (f) => {
    setFormData(f);
    setEditId(f.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSaveAyarlar = async (sections) => {
    if (!isAdmin) {
      toast.error('Bu işlemi sadece yönetici (admin) yapabilir.');
      return;
    }
    setAyarlarSaving(true);
    try {
      const payload = {
        ...sections,
        updated_by: user?.id || null,
        updated_by_name: user?.name || null,
      };
      const res = await axios.put(`${API}/pk/ayarlar`, payload, { withCredentials: true });
      setAyarlar(res.data);
      toast.success('Ayarlar kaydedildi');
    } catch (err) {
      toast.error('Kaydedilemedi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAyarlarSaving(false);
    }
  };

  const handleReset = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Tüm PK ayarları fabrika varsayılanına dönecek. Devam edilsin mi?')) return;
    setAyarlarSaving(true);
    try {
      const res = await axios.post(`${API}/pk/ayarlar/reset`, {}, { withCredentials: true });
      setAyarlar(res.data);
      toast.success('Varsayılanlara döndürüldü');
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAyarlarSaving(false);
    }
  };

  const getTipLabel = (v) => FIRMA_TIPLERI.find(t => t.value === v)?.label || v;

  const getAkreditasyonDurum = (bitis) => {
    if (!bitis) return null;
    const kalan = Math.ceil((new Date(bitis) - new Date()) / 86400000);
    if (kalan <= 0) return { text: 'Süresi Dolmuş', color: 'bg-red-700' };
    if (kalan <= 180) return { text: `${kalan} gün kaldı`, color: 'bg-orange-700' };
    return { text: `${kalan} gün`, color: 'bg-green-700' };
  };

  return (
    <div className="space-y-6" data-testid="pk-ayarlar">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-400" /> Ayarlar
        </h2>
        {!isAdmin && (
          <Badge className="bg-amber-900/30 border border-amber-700/40 text-amber-300 text-[10px]">
            Yalnızca yönetici düzenleyebilir
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t-lg transition-colors ${tab === t.id ? 'bg-gray-800 text-white border-b-2 border-[#E67E22]' : 'text-gray-400 hover:text-white'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Firma Yönetimi */}
      {tab === 'firmalar' && (
        <FirmaTab
          firmalar={firmalar}
          loading={firmaLoading}
          isAdmin={isAdmin}
          getTipLabel={getTipLabel}
          getAkreditasyonDurum={getAkreditasyonDurum}
          onAdd={() => { setFormData({ ...EMPTY_FIRMA }); setEditMode(false); setDialogOpen(true); }}
          onEdit={handleEdit}
          onDelete={handleDeleteFirma}
        />
      )}

      {tab === 'sla' && (
        <SlaTab ayarlar={ayarlar} loading={ayarlarLoading} saving={ayarlarSaving} isAdmin={isAdmin} onSave={handleSaveAyarlar} onReset={handleReset} />
      )}

      {tab === 'periyot' && (
        <PeriyotTab ayarlar={ayarlar} loading={ayarlarLoading} saving={ayarlarSaving} isAdmin={isAdmin} onSave={handleSaveAyarlar} onReset={handleReset} />
      )}

      {tab === 'bildirim' && (
        <BildirimTab ayarlar={ayarlar} loading={ayarlarLoading} saving={ayarlarSaving} isAdmin={isAdmin} onSave={handleSaveAyarlar} onReset={handleReset} />
      )}

      {tab === 'entegrasyon' && (
        <EntegrasyonTab ayarlar={ayarlar} loading={ayarlarLoading} saving={ayarlarSaving} isAdmin={isAdmin} onSave={handleSaveAyarlar} onReset={handleReset} />
      )}

      {tab === 'yapay_zeka' && <YapayZekaTab isAdmin={isAdmin} />}

      {/* Firma Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => setDialogOpen(v)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader><DialogTitle data-testid="firma-form-title">{editMode ? 'Firma Düzenle' : 'Yeni Firma Ekle'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Firma Unvanı *</Label>
              <Input value={formData.unvan} onChange={e => setFormData({ ...formData, unvan: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vergi No</Label>
                <Input value={formData.vergi_no} onChange={e => setFormData({ ...formData, vergi_no: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Firma Tipi</Label>
                <Select value={formData.firma_tipi} onValueChange={v => setFormData({ ...formData, firma_tipi: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {FIRMA_TIPLERI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(formData.firma_tipi === 'a_tipi_muayene' || formData.firma_tipi === 'ekipman_muayene') && (
              <div className="p-3 bg-purple-900/20 border border-purple-700/40 rounded-lg space-y-3">
                <p className="text-xs font-semibold text-purple-400">TÜRKAK Akreditasyon Bilgileri</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Akreditasyon No</Label>
                    <Input value={formData.turkak_akreditasyon_no} onChange={e => setFormData({ ...formData, turkak_akreditasyon_no: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8 text-xs" placeholder="AB-0123-T" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Akreditasyon Bitiş</Label>
                    <Input type="date" value={formData.turkak_akreditasyon_bitis} onChange={e => setFormData({ ...formData, turkak_akreditasyon_bitis: e.target.value })} className="bg-gray-800 border-gray-700 text-white h-8 text-xs" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => { setDialogOpen(false); setFormData({ ...EMPTY_FIRMA }); }} variant="ghost" className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">{submitting ? '...' : editMode ? 'Güncelle' : 'Oluştur'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ FIRMA TAB ============
const FirmaTab = ({ firmalar, loading, isAdmin, getTipLabel, getAkreditasyonDurum, onAdd, onEdit, onDelete }) => {
  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">{firmalar.length} firma kayıtlı</p>
        {isAdmin && (
          <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700" data-testid="add-firma-btn">
            <Plus className="w-4 h-4 mr-2" /> Firma Ekle
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {firmalar.map(f => {
          const akr = getAkreditasyonDurum(f.turkak_akreditasyon_bitis);
          return (
            <Card key={f.id} className="bg-gray-900/80 border-gray-700/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-white">{f.unvan}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">VKN: {f.vergi_no || '—'}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className="bg-blue-900/40 text-blue-300 text-[10px]">{getTipLabel(f.firma_tipi)}</Badge>
                      {f.turkak_akreditasyon_no && (
                        <Badge className="bg-purple-900/40 text-purple-300 text-[10px]">TÜRKAK: {f.turkak_akreditasyon_no}</Badge>
                      )}
                      {akr && <Badge className={`${akr.color} text-[10px]`}>{akr.text}</Badge>}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => onEdit(f)} className="h-7 w-7 p-0 text-gray-400 hover:text-white" data-testid={`firma-duzenle-${f.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(f)} className="h-7 w-7 p-0 text-red-500 hover:text-red-400 hover:bg-red-900/20" data-testid={`firma-sil-${f.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ============ AYARLAR ACTION BAR ============
const ActionBar = ({ isAdmin, saving, onSave, onReset, hasChanges, tabId }) => (
  <div className="flex items-center justify-between p-3 bg-gray-900/60 border border-gray-700/50 rounded-lg" data-testid={`ayarlar-actionbar-${tabId || 'default'}`}>
    <Button onClick={onReset} disabled={!isAdmin || saving} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid={`ayarlar-reset-${tabId || 'default'}`}>
      <RotateCcw className="w-4 h-4 mr-2" /> Varsayılanlara Dön
    </Button>
    <div className="flex items-center gap-2">
      {!hasChanges && <span className="text-xs text-gray-500">Değişiklik yok</span>}
      <Button onClick={onSave} disabled={!isAdmin || saving || !hasChanges} className="bg-green-600 hover:bg-green-700 text-white shadow-lg" data-testid={`ayarlar-kaydet-${tabId || 'default'}`}>
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </Button>
    </div>
  </div>
);

// ============ SLA TAB ============
const SlaTab = ({ ayarlar, loading, saving, isAdmin, onSave, onReset }) => {
  const [sla, setSla] = useState({});
  const [ceza, setCeza] = useState({});
  const [etiket, setEtiket] = useState({});

  useEffect(() => {
    if (ayarlar) {
      setSla(ayarlar.sla || {});
      setCeza(ayarlar.ceza || {});
      setEtiket(ayarlar.etiket_geri_sayim_gun || {});
    }
  }, [ayarlar]);

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const setSlaField = (k, v) => setSla(prev => ({ ...prev, [k]: parseInt(v) || 0 }));
  const setCezaField = (k, v) => setCeza(prev => ({ ...prev, [k]: parseFloat(v) || 0 }));
  const setEtiketField = (k, v) => setEtiket(prev => ({ ...prev, [k]: parseInt(v) || 0 }));

  const hasChanges = JSON.stringify({ sla, ceza, etiket }) !== JSON.stringify({
    sla: ayarlar?.sla || {}, ceza: ayarlar?.ceza || {}, etiket: ayarlar?.etiket_geri_sayim_gun || {}
  });

  return (
    <div className="space-y-4">
      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="sla"
        onSave={() => onSave({ sla, ceza, etiket_geri_sayim_gun: etiket })}
        onReset={onReset} />
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-400" /> Varsayılan SLA Süreleri (Saat)
          </h3>
          <p className="text-xs text-gray-400">Yeni arızalarda sözleşmede özel süre yoksa kullanılacak öncelik bazlı maksimum müdahale süreleri.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'kritik_saat', label: 'Kritik', color: 'text-red-400' },
              { key: 'yuksek_saat', label: 'Yüksek', color: 'text-orange-400' },
              { key: 'orta_saat', label: 'Orta', color: 'text-yellow-400' },
              { key: 'dusuk_saat', label: 'Düşük', color: 'text-green-400' }
            ].map(item => (
              <div key={item.key} className="space-y-1">
                <Label className={`text-xs ${item.color}`}>{item.label}</Label>
                <Input type="number" min="0" value={sla[item.key] ?? ''} onChange={e => setSlaField(item.key, e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid={`sla-${item.key}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" /> Ceza & Kesinti Oranları
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-300">SLA İhlali Saatlik Oran (₺/saat)</Label>
              <Input type="number" min="0" step="0.01" value={ceza.sla_ihlal_saatlik_oran ?? ''} onChange={e => setCezaField('sla_ihlal_saatlik_oran', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid="ceza-saatlik" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-300">SLA İhlali Günlük Oran (₺/gün)</Label>
              <Input type="number" min="0" step="0.01" value={ceza.sla_ihlal_gunluk_oran ?? ''} onChange={e => setCezaField('sla_ihlal_gunluk_oran', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid="ceza-gunluk" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-300">Kullanım Dışı Birim Kesinti (₺)</Label>
              <Input type="number" min="0" step="0.01" value={ceza.kullanim_disi_birim_kesinti ?? ''} onChange={e => setCezaField('kullanim_disi_birim_kesinti', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid="ceza-kullanim-disi" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">Not: Sözleşmede özel oran tanımlıysa o değer öncelikli kullanılır.</p>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-yellow-400" /> Asansör Etiket Geri Sayımı (Gün)
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-red-400">Kırmızı Etiket</Label>
              <Input type="number" min="0" value={etiket.kirmizi ?? ''} onChange={e => setEtiketField('kirmizi', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid="etiket-kirmizi" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-yellow-400">Sarı Etiket</Label>
              <Input type="number" min="0" value={etiket.sari ?? ''} onChange={e => setEtiketField('sari', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9" data-testid="etiket-sari" />
            </div>
          </div>
        </CardContent>
      </Card>

      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="sla-bottom"
        onSave={() => onSave({ sla, ceza, etiket_geri_sayim_gun: etiket })}
        onReset={onReset} />
    </div>
  );
};

// ============ PERIYOT TAB ============
const PeriyotTab = ({ ayarlar, loading, saving, isAdmin, onSave, onReset }) => {
  const [periyotlar, setPeriyotlar] = useState({});
  useEffect(() => { if (ayarlar) setPeriyotlar(ayarlar.periyodik_sureler || {}); }, [ayarlar]);

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const setField = (k, v) => setPeriyotlar(prev => ({ ...prev, [k]: parseInt(v) || 0 }));
  const hasChanges = JSON.stringify(periyotlar) !== JSON.stringify(ayarlar?.periyodik_sureler || {});

  return (
    <div className="space-y-4">
      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="periyot"
        onSave={() => onSave({ periyodik_sureler: periyotlar })}
        onReset={onReset} />
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-400" /> Varsayılan Periyodik Kontrol Süreleri (Ay)
          </h3>
          <p className="text-xs text-gray-400">Yeni eklenen ekipmanların kontrol periyot süresi varsayılan olarak buradan alınır.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(PERIYOT_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                <Label className="text-xs text-gray-300 flex-1">{label}</Label>
                <Input type="number" min="0" value={periyotlar[key] ?? ''} onChange={e => setField(key, e.target.value)} disabled={!isAdmin} className="w-20 h-8 text-xs bg-gray-900 border-gray-700 text-white text-right" data-testid={`periyot-${key}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ BILDIRIM TAB ============
const BildirimTab = ({ ayarlar, loading, saving, isAdmin, onSave, onReset }) => {
  const [bildirim, setBildirim] = useState({});
  useEffect(() => { if (ayarlar) setBildirim(ayarlar.bildirim || {}); }, [ayarlar]);

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const toggle = (key) => setBildirim(prev => ({ ...prev, [key]: !prev[key] }));
  const setNumField = (k, v) => setBildirim(prev => ({ ...prev, [k]: parseInt(v) || 0 }));
  const hasChanges = JSON.stringify(bildirim) !== JSON.stringify(ayarlar?.bildirim || {});

  return (
    <div className="space-y-4">
      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="bildirim"
        onSave={() => onSave({ bildirim })}
        onReset={onReset} />
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-400" /> Bildirim Kanalları
          </h3>
          <p className="text-xs text-gray-400">Her olay tipinde hangi kanalla bildirim atılacağını seçin (uygulama içi, e-posta, SMS).</p>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-400 border-b border-gray-700">
                <tr>
                  <th className="text-left py-2 px-2">Olay</th>
                  <th className="text-center py-2 px-2">Uygulama İçi</th>
                  <th className="text-center py-2 px-2">E-posta</th>
                  <th className="text-center py-2 px-2">SMS</th>
                </tr>
              </thead>
              <tbody>
                {BILDIRIM_OLAYLARI.map(o => (
                  <tr key={o.key} className="border-b border-gray-800/50">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <o.icon className={`w-4 h-4 ${o.color}`} />
                        <span className="text-gray-200">{o.label}</span>
                      </div>
                    </td>
                    <td className="text-center py-3 px-2">
                      <Switch checked={!!bildirim[`${o.key}_in_app`]} onCheckedChange={() => toggle(`${o.key}_in_app`)} disabled={!isAdmin} data-testid={`bildirim-${o.key}-in-app`} />
                    </td>
                    <td className="text-center py-3 px-2">
                      <Switch checked={!!bildirim[`${o.key}_email`]} onCheckedChange={() => toggle(`${o.key}_email`)} disabled={!isAdmin} data-testid={`bildirim-${o.key}-email`} />
                    </td>
                    <td className="text-center py-3 px-2">
                      <Switch checked={!!bildirim[`${o.key}_sms`]} onCheckedChange={() => toggle(`${o.key}_sms`)} disabled={!isAdmin} data-testid={`bildirim-${o.key}-sms`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold text-white">Hatırlatma Zamanlaması</h3>
          <div className="flex items-center gap-3 max-w-md">
            <Label className="text-xs text-gray-300 flex-1">Kontrol/Garanti yaklaşma uyarısı (gün öncesi)</Label>
            <Input type="number" min="0" value={bildirim.hatirlatma_gun_oncesi ?? ''} onChange={e => setNumField('hatirlatma_gun_oncesi', e.target.value)} disabled={!isAdmin} className="w-24 h-8 text-xs bg-gray-800 border-gray-700 text-white text-right" data-testid="bildirim-hatirlatma-gun" />
          </div>
        </CardContent>
      </Card>

      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="bildirim-bottom"
        onSave={() => onSave({ bildirim })}
        onReset={onReset} />
    </div>
  );
};

// ============ ENTEGRASYON TAB ============
const EntegrasyonTab = ({ ayarlar, loading, saving, isAdmin, onSave, onReset }) => {
  const [entegrasyon, setEntegrasyon] = useState({});
  useEffect(() => { if (ayarlar) setEntegrasyon(ayarlar.entegrasyonlar || {}); }, [ayarlar]);

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  const toggle = (key) => setEntegrasyon(prev => ({ ...prev, [key]: !prev[key] }));
  const setField = (k, v) => setEntegrasyon(prev => ({ ...prev, [k]: v }));
  const hasChanges = JSON.stringify(entegrasyon) !== JSON.stringify(ayarlar?.entegrasyonlar || {});

  return (
    <div className="space-y-4">
      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="entegrasyon"
        onSave={() => onSave({ entegrasyonlar: entegrasyon })}
        onReset={onReset} />
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Plug className="w-4 h-4 text-blue-400" /> Dış Sistem Entegrasyonları
          </h3>

          <ToggleRow
            title="EKİPNET Doğrulama"
            desc="Periyodik kontrol rapor yüklemede EKİPNET üzerinden muayeneci kayıt durumu sorgulanır."
            value={!!entegrasyon.ekipnet_aktif}
            onChange={() => toggle('ekipnet_aktif')}
            disabled={!isAdmin}
            testid="entegrasyon-ekipnet"
          />
          <ToggleRow
            title="İSG-KATİP Sözleşme Doğrulama"
            desc="Rapor yüklerken İSG-KATİP üzerinden aktif sözleşme & imza tarihi kontrolü yapılır."
            value={!!entegrasyon.isgkatip_aktif}
            onChange={() => toggle('isgkatip_aktif')}
            disabled={!isAdmin}
            testid="entegrasyon-isgkatip"
          />
          <ToggleRow
            title="TÜRKAK Akreditasyon Kontrolü"
            desc="Muayene kuruluşlarının TÜRKAK akreditasyon süresi rapor yüklemede doğrulanır."
            value={!!entegrasyon.turkak_kontrol_aktif}
            onChange={() => toggle('turkak_kontrol_aktif')}
            disabled={!isAdmin}
            testid="entegrasyon-turkak"
          />

          <div className="pt-2 border-t border-gray-800">
            <Label className="text-xs text-gray-300">TÜRKAK Geçiş Süresi Bitiş Tarihi</Label>
            <Input type="date" value={entegrasyon.turkak_gecis_bitis_tarihi ?? ''} onChange={e => setField('turkak_gecis_bitis_tarihi', e.target.value)} disabled={!isAdmin} className="bg-gray-800 border-gray-700 text-white h-9 mt-1 max-w-xs" data-testid="entegrasyon-turkak-gecis" />
            <p className="text-[11px] text-gray-500 mt-1">Bu tarihten sonra akreditasyonu olmayan firmalar rapor yükleyemez.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4 space-y-2">
          <h3 className="text-sm font-semibold text-white">Mevzuat Bilgileri (Referans)</h3>
          <div className="space-y-1 text-[11px] text-gray-400">
            <p>· 6331 sayılı İSG Kanunu</p>
            <p>· İş Ekipmanlarının Kullanımında Sağlık ve Güvenlik Şartları Yönetmeliği (23 Aralık 2025 revizyonu)</p>
            <p>· Asansör İşletme ve Bakım Yönetmeliği</p>
            <p>· Binaların Yangından Korunması Hakkında Yönetmelik</p>
            <p>· 4734 / 4735 sayılı Kamu İhale Kanunları</p>
          </div>
        </CardContent>
      </Card>

      <ActionBar isAdmin={isAdmin} saving={saving} hasChanges={hasChanges} tabId="entegrasyon-bottom"
        onSave={() => onSave({ entegrasyonlar: entegrasyon })}
        onReset={onReset} />
    </div>
  );
};

const ToggleRow = ({ title, desc, value, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 p-3 bg-gray-800/50 rounded-lg">
    <div className="flex-1">
      <p className="text-sm font-medium text-white">{title}</p>
      <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
    </div>
    <Switch checked={value} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </div>
);
